/* eslint-disable arrow-parens, max-len */
import React, { useState } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap'
import { useToasts } from 'react-toast-notifications'
import { getUserInfo } from '_utils/localData'
import userApi from '_api/user'
import moment from 'moment'
import AddAddressDeliveryModal from './AddAddressDeliveryModal'
import DatePicker from './DatePicker'

const ChangeDeliveryAddress = ({
  isOpen,
  toggle,
  userAddresses,
  selectedAddress,
  onAddressSelected,
  onAddressUpdated,
  onAddressAdded
}) => {
  const { addToast } = useToasts()
  const userInfo = getUserInfo()
  const [selectedAddressId, setSelectedAddressId] = useState(selectedAddress?.id || '')
  const [editingAddress, setEditingAddress] = useState(null)
  const [editForm, setEditForm] = useState({
    name: '',
    address: '',
    postalCode: '',
    phone: '',
    consignee: '',
    isDefault: false
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isAddNewAddressModalOpen, setIsAddNewAddressModalOpen] = useState(false)
  const todayStart = new Date()
  todayStart.setHours(0, 0, 0, 0)

  const parseDeliveryDate = (val) => {
    if (!val) return null
    if (val instanceof Date && !isNaN(val.getTime())) return val
    // DD-MM-YYYY
    const m = moment(val, 'DD-MM-YYYY', true)
    if (m.isValid()) return m.toDate()
    const iso = new Date(val)
    return !isNaN(iso.getTime()) ? iso : null
  }

  const [deliveryDate, setDeliveryDate] = useState(parseDeliveryDate(selectedAddress?.deliveryDate) || todayStart)
  const pickupLimitDate = selectedAddress?.eventDetail?.pickupTimestamp
    ? new Date(selectedAddress.eventDetail.pickupTimestamp * 1000)
    : null
  // const eventPickupTimestamp = (selectedAddress && selectedAddress.eventDetail && selectedAddress.eventDetail.pickupTimestamp) || null
  const fallbackPickupLimit = (() => {
    const addrWithPickup = userAddresses.find(a => a.eventDetail?.pickupTimestamp)
    return addrWithPickup ? new Date(addrWithPickup.eventDetail.pickupTimestamp * 1000) : null
  })()

  const effectivePickupLimit = pickupLimitDate || fallbackPickupLimit || null
  React.useEffect(() => {
    setSelectedAddressId(selectedAddress?.id || '')
    const parsed = parseDeliveryDate(selectedAddress?.deliveryDate)
    if (parsed) {
      setDeliveryDate(parsed)
    }
  }, [selectedAddress])

  React.useEffect(() => {
    if (selectedAddress?.id) {
      setSelectedAddressId(selectedAddress.id)
    } else {
      // Reset if there is no selectedAddress
      setSelectedAddressId('')
    }
  }, [selectedAddress])

  const handleSelectAddress = (address) => {
    setSelectedAddressId(address.id)
    const parsed = parseDeliveryDate(address.deliveryDate)
    if (parsed) {
      setDeliveryDate(prev => prev || parsed)
    }
  }

  const handleConfirm = () => {
    const selected = userAddresses.find(addr => addr.id === selectedAddressId)
    if (!selected) {
      addToast('Please select an address', { appearance: 'error', autoDismiss: true })
      return
    }

    if (!deliveryDate) {
      addToast('Please select delivery date', { appearance: 'error', autoDismiss: true })
      return
    }
    const d = new Date(deliveryDate)
    d.setHours(0, 0, 0, 0)
    const t = new Date()
    t.setHours(0, 0, 0, 0)
    if (d.getTime() < t.getTime()) {
      addToast('Delivery date cannot be in the past', { appearance: 'error', autoDismiss: true })
      return
    }
    if (effectivePickupLimit) {
      const pickupDt = new Date(effectivePickupLimit)
      if (d.getTime() >= pickupDt.getTime()) {
        addToast('Delivery date must be before pickup date', { appearance: 'error', autoDismiss: true })
        return
      }
    }

    const selectedWithDate = { ...selected, deliveryDate: moment(d).format('DD-MM-YYYY') }
    onAddressSelected(selectedWithDate)
    toggle()
  }

  const handleAddNew = () => {
    setIsAddNewAddressModalOpen(true)
  }

  const handleEditClick = (address, e) => {
    e.stopPropagation()
    setEditingAddress(address)
    setEditForm({
      name: address.name,
      address: address.address?.street || address.address,
      postalCode: address.postalCode,
      phone: address.phone,
      consignee: address.consignee || '',
      isDefault: address.isDefault || false
    })
  }

  const renderDeliveryDatePicker = () => (
    <div style={{ marginBottom: '20px' }}>
      <label style={{ fontSize: '14px', fontWeight: 600, marginBottom: 8, display: 'block' }}>
        Delivery Date <span style={{ color: '#dc3545' }}>*</span>
      </label>
      <div className="have-icon" style={{ width: '50%' }}>
        <div style={{ width: '100%' }}>
          <DatePicker
            value={deliveryDate}
            onChange={(d) => setDeliveryDate(d)}
            minDate={todayStart}
            maxDate={effectivePickupLimit ? new Date(effectivePickupLimit.getTime() - 1) : undefined}
            style={{ width: '100%' }}
          />
        </div>
      </div>
    </div>
  )

  const handleDeleteClick = async (addressId, e) => {
    e.stopPropagation()
    if (window.confirm('Are you sure you want to delete this address?')) {
      setIsLoading(true)
      try {
        await userApi.deleteUserAddress(addressId)
        addToast('Address deleted successfully!', { appearance: 'success', autoDismiss: true })

        if (onAddressUpdated) {
          await onAddressUpdated()
        }

        if (selectedAddressId === addressId) {
          setSelectedAddressId('')
        }
      } catch (err) {
        addToast(err?.msgResp || 'Failed to delete address', { appearance: 'error', autoDismiss: true })
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handleEditSave = async () => {
    if (!editForm.name.trim() || !editForm.address.trim() || !editForm.postalCode.trim()) {
      addToast('Please fill in all required fields', { appearance: 'error', autoDismiss: true })
      return
    }

    setIsLoading(true)
    try {
      await userApi.updateUserAddress({
        addressId: editingAddress.id,
        name: editForm.name.trim(),
        address: editForm.address.trim(),
        consignee: editForm.consignee.trim(),
        postalCode: editForm.postalCode.trim(),
        phone: editForm.phone.trim(),
        isDefault: editForm.isDefault
      })

      addToast('Address updated successfully!', { appearance: 'success', autoDismiss: true })
      setEditingAddress(null)

      if (onAddressUpdated) {
        await onAddressUpdated()
      }
    } catch (err) {
      addToast(err?.msgResp || 'Failed to update address', { appearance: 'error', autoDismiss: true })
    } finally {
      setIsLoading(false)
    }
  }

  const handleEditCancel = () => {
    setEditingAddress(null)
    setEditForm({ name: '', address: '', postalCode: '', phone: '', consignee: '', isDefault: false })
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={toggle}
        size="lg"
        centered={true}
        style={{
          maxWidth: '498px'
        }}
      >
        <ModalHeader toggle={toggle}>
          {editingAddress ? 'Edit Address' : 'Delivery Details'}
        </ModalHeader>
        <ModalBody>
          {editingAddress ? (
            <div>
              <div className="form-group mb-3">
                <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                  Address Name <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder="e.g. Home, Office"
                />
              </div>

              <div className="form-group mb-3">
                <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                  Full Address <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={editForm.address}
                  onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                  placeholder="Street, building, unit number"
                />
              </div>

              <div className="form-group mb-3">
                <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                  Postal Code <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={editForm.postalCode}
                  onChange={(e) => setEditForm({ ...editForm, postalCode: e.target.value })}
                  placeholder="123456"
                />
              </div>

              <div className="form-group mb-3">
                <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                  Phone Number
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  placeholder="Phone number"
                />
              </div>

              <div className="form-group mb-3">
                <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                  Consignee
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={editForm.consignee}
                  onChange={(e) => setEditForm({ ...editForm, consignee: e.target.value })}
                  placeholder="Recipient name"
                />
              </div>

              <div className="form-group mb-3 form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="editIsDefault"
                  checked={editForm.isDefault}
                  onChange={(e) => setEditForm({ ...editForm, isDefault: e.target.checked })}
                />
                <label
                  className="form-check-label"
                  htmlFor="editIsDefault"
                  style={{ fontSize: '14px', fontWeight: '500' }}
                >
                  Set as default address
                </label>
              </div>
            </div>
          ) : (
            userAddresses.length > 0 ? (
              <div>
                 {renderDeliveryDatePicker()}
                {userAddresses.map((address) => (
                  <div
                    key={address.id}
                    className={`address-item ${selectedAddressId === address.id ? 'selected' : ''} ${address.isDefault ? 'default' : ''}`}
                    onClick={() => handleSelectAddress(address)}
                  >
                    <div className="d-flex align-items-start justify-content-between">
                      <div className="d-flex align-items-start" style={{ flex: 1 }}>
                        <div style={{ marginTop: '4px' }}>
                          <input
                            type="radio"
                            name="address"
                            className="custom-radio-delivery"
                            checked={selectedAddressId === address.id}
                            onChange={() => handleSelectAddress(address)}
                          />
                        </div>

                        <div style={{ marginLeft: '12px', flex: 1 }}>
                          <div className="d-flex align-items-center mb-2">
                            <span style={{
                              fontSize: '16px',
                              fontWeight: '600',
                              color: '#212529'
                            }}>
                              {address.name}
                            </span>
                            {address.isDefault && (
                              <span className="default-badge" style={{ marginLeft: '8px' }}>Default</span>
                            )}
                          </div>

                          <div style={{
                            fontSize: '14px',
                            color: '#6c757d',
                            lineHeight: '1.4'
                          }}>
                            <div>{address.address?.street || address.address}</div>
                            <div>Consignee: {address.consignee}</div>
                            <div>Postal Code: {address.postalCode}</div>
                            <div>Phone: {address.phone}</div>
                          </div>
                        </div>
                      </div>

                      {/* Edit and Delete */}
                      <div className="d-flex align-items-center" style={{ gap: '8px', marginLeft: '8px' }}>
                        <i
                          className="icon_pencil-edit"
                          style={{
                            cursor: 'pointer',
                            color: '#079d9a',
                            fontSize: '16px',
                            padding: '4px'
                          }}
                          onClick={(e) => handleEditClick(address, e)}
                          title="Edit address"
                        />
                        <i
                          className="icon_trash"
                          style={{
                            cursor: 'pointer',
                            color: 'grey',
                            fontSize: '16px',
                            padding: '4px'
                          }}
                          onClick={(e) => handleDeleteClick(address.id, e)}
                          title="Delete address"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <div
                 className="mt-0 pt-1"
                  style={{
                    // borderTop: '1px solid #e9ecef',
                    textAlign: 'right'
                  }}
                >
                  <span
                    onClick={handleAddNew}
                    style={{
                      fontSize: '14px',
                      color: '#079d9a',
                      textDecoration: 'underline',
                      cursor: 'pointer'
                    }}
                  >
                    Add New Address
                  </span>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <p style={{ color: '#6c757d', marginBottom: '16px', fontSize: '19px' }}>
                  You don&#39;t have any saved addresses yet.
                </p>
                <button
                  type="button"
                  className="btn btn-primary mx-auto"
                  onClick={handleAddNew}
                  style={{ borderRadius: '23px', width: '80%' }}
                >
                  Add Your First Address
                </button>
              </div>
            )
          )}
        </ModalBody>
        <ModalFooter style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editingAddress ? (
            <div style={{ display: 'flex', gap: '12px', width: '100%', justifyContent: 'flex-end' }}>
              <Button color="light" onClick={handleEditCancel} disabled={isLoading} style={{ border: '1px solid #07C3C3' }}>
                <span style={{ color: '#07C3C3' }}>Cancel</span>
              </Button>
              <Button
                color="primary"
                onClick={handleEditSave}
                disabled={isLoading}
                style={{ borderRadius: '25px' }}
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          ) : (
            userAddresses.length > 0 && (
              <div style={{ display: 'flex', gap: '12px', width: '100%', justifyContent: 'flex-end' }}>
                <Button color="light" onClick={toggle} style={{ border: '1px solid #07C3C3' }}>
                  <span style={{ color: '#07C3C3' }}>Cancel</span>
                </Button>
                <Button
                  color="primary"
                  onClick={handleConfirm}
                  disabled={!selectedAddressId}
                  style={{ borderRadius: '25px' }}
                >
                  Submit
                </Button>
              </div>
            )
          )}
        </ModalFooter>
      </Modal>
      {userInfo?.phone && (
        <AddAddressDeliveryModal
          isOpen={isAddNewAddressModalOpen}
          toggle={() => setIsAddNewAddressModalOpen(false)}
          onAddressAdded={async (newAddressObj) => {
            setIsAddNewAddressModalOpen(false)
            toggle()
            if (onAddressAdded) {
              await onAddressAdded(newAddressObj)
            }
          }}
          phoneNumber={userInfo?.phone || ''}
          userAddresses={userAddresses}
        />
      )}
    </>
  )
}

export default ChangeDeliveryAddress
